<template>
  <li class="poster-card">
    <!-- Restricted content overlay -->
    <div
      v-if="item.restricted === true"
      class="poster-card__restriction"
      @click="isHidden = !isHidden"
    >
      <div v-if="!isHidden" class="poster-card__restriction-msg" role="alert">
        תוכן זה חסום בשל הגדרות הפרופיל שלך
      </div>
    </div>

    <ui-card
      variant="poster"
      :to="item.restricted ? undefined : url"
      :clickable="item.restricted === true"
      @click="item.restricted ? (isHidden = !isHidden) : null"
      class="poster-card__inner"
      :class="{ 'poster-card__inner--restricted': item.restricted }"
    >
      <!-- Media slot -->
      <template #media>
        <img
          v-if="lazy"
          v-lazy="item.pic_thumb_parsed"
          :alt="item.title"
          class="poster-card__img"
        />
        <img
          v-else
          :src="item.pic_thumb_parsed"
          :alt="item.title"
          class="poster-card__img"
          loading="lazy"
        />

        <!-- Badge — sticker label -->
        <ui-badge
          v-if="item.sticker > 0"
          variant="accent"
          class="poster-card__badge"
        >{{ $store.state.stickers[item.sticker].name }}</ui-badge>

        <!-- Badge — new/quality from API if present -->
        <ui-badge
          v-else-if="item.badge"
          variant="accent"
          class="poster-card__badge"
        >{{ item.badge }}</ui-badge>

        <!-- Progress bar (continue watching) -->
        <div
          v-if="item.user_duration_perc > 0"
          class="poster-card__progress-track"
          role="progressbar"
          :aria-valuenow="item.user_duration_perc"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="'צפייה ' + item.user_duration_perc + '%'"
        >
          <div
            class="poster-card__progress-fill"
            :style="{ width: item.user_duration_perc + '%' }"
          ></div>
        </div>

        <!-- Resume hint: shown only for in-progress items -->
        <div
          v-if="item.user_duration_perc > 0"
          class="poster-card__resume"
        >המשך מ-{{ resumeTime }}</div>
      </template>

      <!-- Title + meta below image (always visible, fixed height) -->
      <div class="poster-card__caption">
        <div class="poster-card__title">{{ item.title }}</div>
        <div class="poster-card__meta" v-if="item.main_category || item.year || item.type">
          <span v-if="item.main_category">{{ item.main_category.name }}</span>
          <span v-if="item.main_category && (item.year || item.type)"> · </span>
          <span v-if="item.year">{{ item.year }}</span>
          <span v-else-if="item.type">{{ typeLabel }}</span>
        </div>
      </div>
    </ui-card>

    <!-- Netflix-style hover preview panel (absolute, no layout push) -->
    <div class="poster-card__hover-panel" aria-hidden="true">
      <div class="poster-card__hover-title">{{ item.title }}</div>
      <div class="poster-card__hover-meta">
        <span v-if="item.type" class="poster-card__pill">{{ typeLabel }}</span>
        <span v-if="durationLabel">{{ durationLabel }}</span>
        <span v-if="item.year">{{ item.year }}</span>
        <span v-if="item.main_category">{{ item.main_category.name }}</span>
      </div>
      <div class="poster-card__desc" v-if="item.description">{{ item.description }}</div>
    </div>
  </li>
</template>

<script>
import UiCard from './ui/Card.vue';
import UiBadge from './ui/Badge.vue';

export default {
  components: { UiCard, UiBadge },
  props: {
    item: { type: Object, required: true },
    lazy: { type: Boolean, default: true },
  },

  data() {
    return {
      isHidden: true,
      url:
        this.item.restricted === true
          ? ''
          : (this.item.type == 'live' ? '/livePage/' : '/item/') + this.item.id,
    };
  },

  computed: {
    resumeTime() {
      var secs = parseInt(this.item.user_duration || 0, 10);
      var m = Math.floor(secs / 60);
      var s = secs % 60;
      return (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s);
    },

    typeLabel() {
      if (this.item.type === 'live') return 'שידור חי';
      if (this.item.type === 'series') return 'סדרה';
      return 'סרט';
    },

    durationLabel() {
      if (this.item.duration_formatted) return this.item.duration_formatted;
      var d = Number(this.item.duration);
      if (!d || d <= 0) return '';
      var mins = Math.round(d / 60);
      if (mins < 60) return mins + ' דק׳';
      var h = Math.floor(mins / 60);
      var rem = mins % 60;
      return rem ? (h + ' ש׳ ' + rem + ' דק׳') : (h + ' ש׳');
    },

    restrictions_list() {
      var ret = [];
      var restrictions = JSON.parse(JSON.stringify(this.$store.state.restrictions));
      for (let i in this.item.restriction_ids_parsed) {
        for (let z in restrictions) {
          if (restrictions[z].id == this.item.restriction_ids_parsed[i]) ret.push(restrictions[z]);
        }
      }
      return ret;
    },
  },

  methods: {
    toggle_fav() {
      this.$emit('toggle-favorite', this.item.id);
    },
  },
};
</script>

<style scoped lang="scss">
/* ── List item reset ── */
.poster-card {
  list-style: none;
  flex-shrink: 0;
  scroll-snap-align: start;
  transition: opacity 200ms ease, box-shadow 220ms ease;
  position: relative;
  border-radius: var(--radius-md);
}
.poster-card:hover {
  z-index: 10;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
}

/* ── Inner card sizing — responsive via CSS custom props set on :root ── */
.poster-card__inner {
  width: calc(
    (100vw - var(--rail-padding-inline, 36px) * 2 - var(--rail-gap, 6px) * (var(--cards-per-row, 6) - 1))
    / var(--cards-per-row, 6)
  );
  min-width: 110px;
  max-width: 320px;
}

.poster-card__inner--restricted {
  opacity: 0.5;
}

/* ── Thumbnail ── */
.poster-card__img {
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  object-fit: cover;
  display: block;
  border-radius: var(--radius-md);
}

/* ── Badge — top-start corner ── */
.poster-card__badge {
  position: absolute;
  top: 8px;
  inset-inline-start: 8px;
  z-index: 1;
}

/* ── Progress bar — Netflix red ── */
.poster-card__progress-track {
  position: absolute;
  bottom: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  height: 3px;
  background: rgba(255,255,255,0.25);
  border-radius: 0 0 var(--radius-md) var(--radius-md);
  z-index: 2;
}
.poster-card__progress-fill {
  height: 100%;
  background: #E50914;
  border-radius: inherit;
}

/* Resume hint */
.poster-card__resume {
  position: absolute;
  top: var(--space-3);
  inset-inline-start: var(--space-3);
  background: rgba(0,0,0,0.7);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-family: var(--font-hebrew);
  color: var(--color-text);
  white-space: nowrap;
  z-index: 3;
}

/* ── Caption below poster (always visible, fixed height) ── */
.poster-card__caption {
  padding: 8px 4px 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.poster-card__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-hebrew);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.25;
  letter-spacing: -0.2px;
}

.poster-card__meta {
  font-size: 11px;
  color: var(--color-textMuted, rgba(255,255,255,0.6));
  font-family: var(--font-hebrew);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Hover preview panel — absolute overlay within card bounds (no layout push, no overflow clipping) ── */
.poster-card__hover-panel {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 38%, rgba(0,0,0,0.92) 75%, rgba(0,0,0,0.96) 100%);
  border-radius: var(--radius-md);
  padding: 12px 14px 14px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 180ms ease, visibility 0s linear 180ms;
  z-index: 4;
  pointer-events: none;
}
.poster-card:hover .poster-card__hover-panel {
  opacity: 1;
  visibility: visible;
  transition: opacity 180ms ease, visibility 0s;
}

.poster-card__hover-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text);
  font-family: var(--font-hebrew);
  line-height: 1.25;
  letter-spacing: -0.2px;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.poster-card__hover-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  font-size: 11px;
  color: var(--color-textMuted, rgba(255,255,255,0.75));
  font-family: var(--font-hebrew);
}

.poster-card__pill {
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.18);
  color: var(--color-text);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.poster-card__desc {
  font-size: 12px;
  color: var(--color-textMuted, rgba(255,255,255,0.78));
  font-family: var(--font-hebrew);
  line-height: 1.45;
  margin-top: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Hide hover panel on touch devices — relies on hover */
@media (hover: none) {
  .poster-card__hover-panel {
    display: none;
  }
}

/* Mobile breakpoint — caption tighter, smaller text */
@media (max-width: 640px) {
  .poster-card__caption {
    padding: 6px 2px 0;
  }
  .poster-card__title {
    font-size: 12px;
  }
  .poster-card__meta {
    font-size: 10px;
  }
}

/* ── Restriction overlay ── */
.poster-card__restriction {
  position: absolute;
  inset: 0;
  z-index: 10;
  cursor: pointer;
}
.poster-card__restriction-msg {
  position: absolute;
  bottom: 8px;
  inset-inline-start: 8px;
  inset-inline-end: 8px;
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 8px;
  font-size: 13px;
  color: var(--color-text);
  font-family: var(--font-hebrew);
  text-align: start;
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .poster-card {
    transition: none;
  }
}
</style>
